import { makeAutoObservable } from "mobx";

import { RootStore } from "store/root.store";
import {
  fetchStreamingIntegrations,
  createStreamingIntegration,
  fetchStreamingAPIKeys,
  rotateStreamingAPIKey,
  deleteStreamingIntegration,
} from "libs/api/methods";
import { StreamingIntegration, StreamingAPIKey, FEATURE } from "store/types";
import { copyToClipboard } from "tools";

export class StreamingIntegrationsStore {
  integrations: StreamingIntegration[] = [];
  keys: Record<string, StreamingAPIKey[]> = {};

  constructor(readonly rootStore: RootStore) {
    this.rootStore = rootStore;

    this.fetchIntegrations = this.fetchIntegrations.bind(this);
    this.createIntegration = this.createIntegration.bind(this);
    this.fetchKeys = this.fetchKeys.bind(this);
    this.rotateKey = this.rotateKey.bind(this);
    this.deleteIntegration = this.deleteIntegration.bind(this);

    makeAutoObservable(this);
  }

  async fetchIntegrations() {
    try {
      if (this.rootStore.auth.isFeatureEnabled(FEATURE.API_ACCESS)) {
        this.integrations = await fetchStreamingIntegrations();
      }
    } catch {}
  }

  async createIntegration(name: string) {
    // eslint-disable-next-line camelcase
    const { integration, consumer_key } = await createStreamingIntegration({ name });

    this.integrations.push(integration);

    // Also fetch the keys for this integration
    await this.fetchKeys(integration.id);

    const keyId = this.getKeysForIntegration(integration.id)?.[0]?.id || "";

    // eslint-disable-next-line camelcase
    return { key: consumer_key, keyId };
  }

  async fetchKeys(integrationId: string) {
    try {
      const keys = await fetchStreamingAPIKeys(integrationId);
      this.keys[integrationId] = keys;
    } catch {}
  }

  async rotateKey(integrationId: string, keyId: string) {
    const { key, details } = await rotateStreamingAPIKey(integrationId, keyId);

    // Update the keys in the store
    if (this.keys[integrationId]) {
      this.keys[integrationId] = this.keys[integrationId].map((k) => (k.id === details.id ? details : k));
    }

    copyToClipboard({
      value: key,
      title: "New streaming key is copied:",
      message: key,
      duration: 10000,
    });

    return key;
  }

  async deleteIntegration(integrationId: string) {
    await deleteStreamingIntegration(integrationId);

    // Remove from local state
    this.integrations = this.integrations.filter((i) => i.id !== integrationId);
    delete this.keys[integrationId];
  }

  getKeysForIntegration(integrationId: string): StreamingAPIKey[] {
    return this.keys[integrationId] || [];
  }
}
